import { setCenteredMaxWidth } from 'js/helpers.js';

/**
 * Returns a function that listens to form submission events on a pagination
 * form and calls pagination handlers accordingly
 * @param {Object} funcs
 * @param {function} funcs.handlePrevButton
 * @param {function} funcs.handleNextButton
 * @param {(input: number) => void} funcs.handleInput
 * @param {function?} funcs.then - Optional: execute after any action
 * @returns {(ev: SubmitEvent) => void}
 */
export const handlePagination = ({
    handlePrevButton, handleNextButton, handleInput, then
}) => (ev) => {
    ev.preventDefault();
    switch (ev.submitter.name) {
    case 'prev': {
        handlePrevButton();
        break;
    }
    case 'next': {
        handleNextButton();
        break;
    }
    case 'input-submitter': {
        const inputEl = ev.currentTarget.elements.input;

        if (!inputEl.validity.valid) break;

        handleInput(Number(inputEl.value));
        break;
    }
    default: {
        // do nothing
    }
    }
    then?.(); // eslint-disable-line no-unused-expressions
};

/**
 * @param {Element} formEl
 * */
export const addPaginationInputListeners = (formEl) => {
    formEl.querySelector('.filter__pagination-input').addEventListener('input', (event) => {
        const input = event.target;
        const inputError = input.parentElement?.querySelector('.filter__pagination-error');

        if (!inputError) return;

        if (!inputError.classList.contains('is-visible')) {
            return;
        }
        if (input.validity.valid) {
            inputError.classList.remove('is-visible');
            inputError.style.removeProperty('--pagination-error-max-width');
        } else {
            input.checkValidity();
        }
    });

    formEl.querySelector('.filter__pagination-input').addEventListener('invalid', (event) => {
        event.preventDefault();
        const input = event.target;
        const inputError = input.parentElement?.querySelector('.filter__pagination-error');
        const errorText = inputError?.querySelector('.filter__pagination-error-text');

        if (!inputError || !errorText) return;

        inputError.classList.add('is-visible');
        const { validity } = input;

        if (validity.rangeUnderflow) {
            errorText.textContent = `${input.dataset.errorMin}  ${input.min}`;
        } else if (validity.rangeOverflow) {
            errorText.textContent = `${input.dataset.errorMax}  ${input.max}`;
        } else if (validity.badInput || validity.typeMismatch) {
            errorText.textContent = input.dataset.errorNumber;
        } else {
            errorText.textContent = input.validationMessage;
        }

        setCenteredMaxWidth(input, inputError, '--pagination-error-max-width');

        if (!input.restorePaginationHandler) {
            input.restorePaginationHandler = (e) => {
                if (!input.contains(e.target)) {
                    if (inputError.classList.contains('is-visible')) {
                        input.value = input.defaultValue;
                        inputError.classList.remove('is-visible');
                        inputError.style.removeProperty('--pagination-error-max-width');
                    }
                    document.removeEventListener('pointerdown', input.restorePaginationHandler);
                    input.restorePaginationHandler = null;
                }
            };
            setTimeout(() => document.addEventListener('pointerdown', input.restorePaginationHandler), 0);
        }
    });

    formEl.querySelector('.filter__pagination-input').addEventListener('blur', (event) => {
        const input = event.target;

        if (!input.validity.valid) {
            input.checkValidity();
        } else if (input.value !== input.defaultValue) {
            formEl.requestSubmit(formEl.querySelector('[name="input-submitter"]'));
        }
    });
};

/**
 * @param {Element} formEl
 * @param {{ currentPageNum: number, pageCount: number }} options
 */
export const updatePagination = (formEl, { currentPageNum, pageCount }) => {
    const prevButton = formEl.querySelector('[name="prev"]');
    const nextButton = formEl.querySelector('[name="next"]');
    const input = formEl.querySelector('[name="input"]');
    const count = formEl.querySelector('[data-name="page-count"]');

    prevButton.disabled = currentPageNum <= 1;
    nextButton.disabled = currentPageNum >= pageCount;
    input.value = currentPageNum;
    input.defaultValue = currentPageNum;
    input.max = pageCount;
    count.textContent = pageCount;
};
